import { defineTextStyles } from '@chakra-ui/react';

export const textStyles = defineTextStyles({
  '2xs': {
    value: {
      fontSize: '2xs',
      lineHeight: '0.75rem',
    },
  },
  xs: {
    value: {
      fontSize: 'xs',
      lineHeight: '1rem',
    },
  },
  sm: {
    value: {
      fontSize: 'sm',
      lineHeight: '1.25rem',
    },
  },
  md: {
    value: {
      fontSize: 'md',
      lineHeight: '1.5rem',
    },
  },
  lg: {
    value: {
      fontSize: 'lg',
      lineHeight: '1.75rem',
    },
  },
  xl: {
    value: {
      fontSize: 'xl',
      lineHeight: '1.875rem',
    },
  },
  '2xl': {
    value: {
      fontSize: '2xl',
      lineHeight: '2rem',
    },
  },
  '3xl': {
    value: {
      fontSize: '3xl',
      lineHeight: '2.375rem',
    },
  },
  '4xl': {
    value: {
      fontSize: '4xl',
      lineHeight: '2.75rem',
      letterSpacing: '-0.025em',
    },
  },
  none: {
    value: {},
  },

  // ── Semantic text styles (typography only, no color) ────────────────
  // Color via fg.* semantic tokens: <Text textStyle="title" color="fg.muted">
  //
  // heading  — xl/700  — page titles
  // subtitle — xl/600  — dialog headers, drawer headers, section titles
  // lead     — lg/700  — prominent block headings, between section and heading
  // prose    — md/400  — text meant to be READ: chat messages, dialog copy
  // section  — md/600  — card and section titles (settings cards, panels)
  // nav      — md/500  — sidebar nav, tab labels
  // strong   — sm/700  — emphasis inside text, numbers on game stat cards
  // title    — sm/600  — section headings, game names, labels, dates
  // body     — sm/400  — descriptions, comments, meta text
  // label    — xs/600  — compact labels and chips
  // caption  — xs/500  — small meta (duration, members, time)
  // badge    — xs/700  — status badges (LIVE, Upcoming, counters)
  // micro    — 2xs/700 — tiny badges, tab counters
  // fine     — 2xs/400 — tiny meta: timestamps, counters, link hosts
  //
  // prose и body — не одно и то же. body это описание рядом с объектом: его сканируют.
  // prose это сам объект: сообщение в чате, текст модалки, вопрос онбординга — то, ради чего
  // человек сюда пришёл, и что он читает строку за строкой. Отсюда и кегль на ступень крупнее.
  //
  // fine и micro тоже разные: micro это плашка (вес 700, капслок-подобная), fine это шёпот —
  // время под сообщением, счётчик символов, хост ссылки. Одна ступень, противоположный вес.

  heading: {
    value: {
      fontSize: 'xl',
      lineHeight: '1.875rem',
      fontWeight: '700',
    },
  },
  subtitle: {
    value: {
      fontSize: 'xl',
      lineHeight: '1.875rem',
      fontWeight: '600',
    },
  },
  lead: {
    value: {
      fontSize: 'lg',
      lineHeight: '1.75rem',
      fontWeight: '700',
    },
  },
  prose: {
    value: {
      fontSize: 'md',
      lineHeight: '1.5rem',
      fontWeight: '400',
    },
  },
  section: {
    value: {
      fontSize: 'md',
      lineHeight: '1.5rem',
      fontWeight: '600',
    },
  },
  nav: {
    value: {
      fontSize: 'md',
      lineHeight: '1.5rem',
      fontWeight: '500',
    },
  },
  strong: {
    value: {
      fontSize: 'sm',
      lineHeight: '1.25rem',
      fontWeight: '700',
    },
  },
  title: {
    value: {
      fontSize: 'sm',
      lineHeight: '1.25rem',
      fontWeight: '600',
    },
  },
  body: {
    value: {
      fontSize: 'sm',
      lineHeight: '1.25rem',
      fontWeight: '400',
    },
  },
  label: {
    value: {
      fontSize: 'xs',
      lineHeight: '1rem',
      fontWeight: '600',
    },
  },
  caption: {
    value: {
      fontSize: 'xs',
      lineHeight: '1rem',
      fontWeight: '500',
    },
  },
  badge: {
    value: {
      fontSize: 'xs',
      lineHeight: '1rem',
      fontWeight: '700',
    },
  },
  micro: {
    value: {
      fontSize: '2xs',
      lineHeight: '0.75rem',
      fontWeight: '700',
    },
  },
  fine: {
    value: {
      fontSize: '2xs',
      lineHeight: '0.75rem',
      fontWeight: '400',
    },
  },

  // ── Article scale (long-form reading: blog) ─────────────────────────
  //
  // article.h1    — 3xl→5xl/800 — заголовок статьи, один на страницу
  // article.h2    — 2xl→3xl/700 — раздел статьи
  // article.h3    — xl→2xl/600  — подраздел
  // article.h4    — lg→xl/600   — мелкий подзаголовок и заголовок плашки внутри статьи
  // article.body  — md→lg/400   — абзацы, списки, текст плашек
  // article.quote — lg→xl/400   — цитата, курсивом
  //
  // Это не prose, и разница не в кегле. prose — текст, который читают в интерфейсе: сообщение,
  // окно, вопрос онбординга — экран, от силы абзац, и интерлиньяж 1.5 там в самый раз. Статья —
  // тысячи знаков подряд в колонке 720px, и тот же 1.5 на 18px читается плотно: глаз теряет
  // строку при переходе. Отсюда 1.7 на десктопе и 1.65 на телефоне, где кегль на ступень ниже.
  //
  // Адаптивность лежит в самом стиле, а не в вызове. `textStyle={{ base: 'md', md: 'lg' }}`
  // в вызове тянет с собой lineHeight сырой ступени и молча перебивает `lineHeight="1.75"`,
  // стоящий рядом, — так статьи на проде и оказались на 1.5 вместо 1.75, хотя в коде стояло
  // 1.75. Один стиль на вызов — и перебивать нечему.
  //
  // Заголовки держат ступень между собой на обоих экранах: на телефоне h1 3xl (30px), h2 2xl
  // (24px), h3 xl (20px), h4 lg (18px) — раздел статьи никогда не равен её названию.
  // Интерлиньяж заголовков ужимается с ростом кегля: 1.25 у h2, где строка в 30px и так
  // высока, до 1.35 у h4, который стоит вплотную к абзацу и не должен слипаться с ним.
  article: {
    h1: {
      value: {
        fontSize: { base: '3xl', md: '5xl' },
        lineHeight: { base: '1.2', md: '1.15' },
        fontWeight: '800',
      },
    },
    h2: {
      value: {
        fontSize: { base: '2xl', md: '3xl' },
        lineHeight: '1.25',
        fontWeight: '700',
      },
    },
    h3: {
      value: {
        fontSize: { base: 'xl', md: '2xl' },
        lineHeight: '1.3',
        fontWeight: '600',
      },
    },
    h4: {
      value: {
        fontSize: { base: 'lg', md: 'xl' },
        lineHeight: '1.35',
        fontWeight: '600',
      },
    },
    body: {
      value: {
        fontSize: { base: 'md', md: 'lg' },
        lineHeight: { base: '1.65', md: '1.7' },
        fontWeight: '400',
      },
    },
    quote: {
      value: {
        fontSize: { base: 'lg', md: 'xl' },
        lineHeight: '1.6',
        fontWeight: '400',
        fontStyle: 'italic',
      },
    },
  },

  // Editorial previews keep their current hierarchy, separate from long-form articles.
  blog: {
    listHeading: {
      value: { fontSize: { base: '3xl', md: '4xl' }, fontWeight: '800', lineHeight: '1.2' },
    },
    featuredTitle: {
      value: { fontSize: { base: '2xl', md: '3xl' }, fontWeight: '800', lineHeight: '1.2' },
    },
    cardTitle: { value: { fontSize: 'lg', fontWeight: '700', lineHeight: '1.3' } },
    featuredDescription: { value: { fontSize: 'md', fontWeight: '400', lineHeight: '1.6' } },
    cardDescription: { value: { fontSize: 'sm', fontWeight: '400', lineHeight: '1.5' } },
  },

  // Public-page display copy has a different reading scale from the application UI.
  // These roles preserve the existing marketing hierarchy across all three landings.
  marketing: {
    hero: {
      value: {
        fontSize: { base: '4xl', md: '6xl', xl: '7xl' },
        fontWeight: '900',
        lineHeight: '0.95',
        letterSpacing: '-0.03em',
      },
    },
    heroCompact: {
      value: {
        fontSize: { base: '4xl', md: '6xl' },
        fontWeight: '900',
        lineHeight: '0.95',
        letterSpacing: '-0.03em',
      },
    },
    section: {
      value: {
        fontSize: { base: '3xl', lg: '5xl' },
        fontWeight: '900',
        lineHeight: '1.05',
        letterSpacing: '-0.02em',
      },
    },
    cta: {
      value: {
        fontSize: { base: '3xl', md: '5xl' },
        fontWeight: '900',
        lineHeight: '1.02',
        letterSpacing: '-0.02em',
      },
    },
    feature: {
      value: {
        fontSize: { base: '2xl', md: '4xl' },
        fontWeight: '900',
        lineHeight: '1.1',
        letterSpacing: '-0.02em',
      },
    },
    stat: {
      value: {
        fontSize: { base: '2xl', md: '4xl' },
        fontWeight: '900',
        lineHeight: '1',
        fontVariantNumeric: 'tabular-nums',
      },
    },
    card: { value: { fontSize: { base: 'lg', md: 'xl' }, fontWeight: '800', lineHeight: '1.25' } },
    smallHeading: { value: { fontSize: 'md', fontWeight: '800', lineHeight: '1.5' } },
    intro: { value: { fontSize: { base: 'md', md: 'lg' }, fontWeight: '400', lineHeight: '1.7' } },
    body: { value: { fontSize: 'sm', fontWeight: '400', lineHeight: '1.6' } },
    subtitle: {
      value: { fontSize: { base: 'sm', md: 'md' }, fontWeight: '400', lineHeight: '1.5' },
    },
    faqQuestion: {
      value: { fontSize: { base: 'md', md: 'lg' }, fontWeight: '700', lineHeight: '1.5' },
    },
    faqAnswer: { value: { fontSize: 'sm', fontWeight: '400', lineHeight: '1.8' } },
  },
});
